// src/theme/themeLoader.ts
import { themes } from "./themes";
import { productionTheme } from "./productionTheme";
import type { Theme } from "./theme";

/**
 * loadTheme
 * - Accepts either a theme name (string) or a theme object.
 * - If a theme object is passed and it contains all required keys, return it.
 * - Otherwise if a name is passed, resolve it from the themes index and validate.
 * - If validation fails, return productionTheme as the safe fallback.
 */
export function loadTheme(nameOrObj: string | any): Theme {
  // If caller passed an object, ensure it has the required keys
  if (typeof nameOrObj === "object" && nameOrObj !== null) {
    const candidate = nameOrObj as Theme;
    const missing = Object.keys(productionTheme).filter((k) => !(k in candidate));
    if (missing.length === 0) {
      return candidate;
    } else {
      console.warn(`[ThemeLoader] Provided theme object missing keys: ${missing.join(", ")} — reverting to production theme.`);
      return productionTheme;
    }
  }

  // Treat input as a theme name
  const name = String(nameOrObj);
  const theme = (themes as any)[name];

  if (!theme) {
    console.warn(`[ThemeLoader] Theme "${name}" not found. Reverting to production theme.`);
    return productionTheme;
  }

  const missing = Object.keys(productionTheme).filter((k) => !(k in theme));
  if (missing.length > 0) {
    console.warn(`[ThemeLoader] Theme "${name}" missing keys: ${missing.join(", ")} — reverting to production theme.`);
    return productionTheme;
  }

  return theme as Theme;
}
